/**
 * Permissions API Client
 * Endpoints for permission management (current user, company-level, user-level)
 */

import { apiClient } from "./client";
import type {
  EffectivePermissions,
  CompanyModulesResponse,
  CompanyPlantsResponse,
  CompanyChargingStationsResponse,
  CompanyEVIProducersResponse,
  CompanyEVIConsumersResponse,
  UserModulesResponse,
  UserPlantsResponse,
  UserChargingStationsResponse,
  UserEVIProducersResponse,
  UserEVIConsumersResponse,
} from "./types";

// ============================================================================
// Current User
// ============================================================================

/**
 * Get effective permissions for the currently authenticated user
 */
export async function getMyPermissions(): Promise<EffectivePermissions> {
  return apiClient.get<EffectivePermissions>("/api/v1/permissions/me");
}

// ============================================================================
// Company-Level Permissions (admin only)
// ============================================================================

/**
 * Get modules assigned to a company
 */
export async function getCompanyModules(
  companyId: number
): Promise<CompanyModulesResponse> {
  return apiClient.get<CompanyModulesResponse>(
    `/api/v1/permissions/companies/${companyId}/modules`
  );
}

/**
 * Set modules for a company
 */
export async function setCompanyModules(
  companyId: number,
  modules: string[]
): Promise<CompanyModulesResponse> {
  return apiClient.put<CompanyModulesResponse>(
    `/api/v1/permissions/companies/${companyId}/modules`,
    { modules }
  );
}

/**
 * Get plants assigned to a company
 */
export async function getCompanyPlants(
  companyId: number
): Promise<CompanyPlantsResponse> {
  return apiClient.get<CompanyPlantsResponse>(
    `/api/v1/permissions/companies/${companyId}/plants`
  );
}

/**
 * Set plants for a company
 */
export async function setCompanyPlants(
  companyId: number,
  plantIds: number[]
): Promise<CompanyPlantsResponse> {
  return apiClient.put<CompanyPlantsResponse>(
    `/api/v1/permissions/companies/${companyId}/plants`,
    { plant_ids: plantIds }
  );
}

/**
 * Get charging stations assigned to a company
 */
export async function getCompanyChargingStations(
  companyId: number
): Promise<CompanyChargingStationsResponse> {
  return apiClient.get<CompanyChargingStationsResponse>(
    `/api/v1/permissions/companies/${companyId}/charging-stations`
  );
}

/**
 * Set charging stations for a company
 */
export async function setCompanyChargingStations(
  companyId: number,
  stationIds: number[]
): Promise<CompanyChargingStationsResponse> {
  return apiClient.put<CompanyChargingStationsResponse>(
    `/api/v1/permissions/companies/${companyId}/charging-stations`,
    { charging_station_ids: stationIds }
  );
}

/**
 * Get EVI producers assigned to a company
 */
export async function getCompanyEVIProducers(
  companyId: number
): Promise<CompanyEVIProducersResponse> {
  return apiClient.get<CompanyEVIProducersResponse>(
    `/api/v1/permissions/companies/${companyId}/evi-producers`
  );
}

/**
 * Set EVI producers for a company
 */
export async function setCompanyEVIProducers(
  companyId: number,
  ids: number[]
): Promise<CompanyEVIProducersResponse> {
  return apiClient.put<CompanyEVIProducersResponse>(
    `/api/v1/permissions/companies/${companyId}/evi-producers`,
    { evi_producer_ids: ids }
  );
}

/**
 * Get EVI consumers assigned to a company
 */
export async function getCompanyEVIConsumers(
  companyId: number
): Promise<CompanyEVIConsumersResponse> {
  return apiClient.get<CompanyEVIConsumersResponse>(
    `/api/v1/permissions/companies/${companyId}/evi-consumers`
  );
}

/**
 * Set EVI consumers for a company
 */
export async function setCompanyEVIConsumers(
  companyId: number,
  ids: number[]
): Promise<CompanyEVIConsumersResponse> {
  return apiClient.put<CompanyEVIConsumersResponse>(
    `/api/v1/permissions/companies/${companyId}/evi-consumers`,
    { evi_consumer_ids: ids }
  );
}

// ============================================================================
// User-Level Permissions (admin + manager)
// ============================================================================

/**
 * Get modules assigned to a user
 */
export async function getUserModules(
  userId: number
): Promise<UserModulesResponse> {
  return apiClient.get<UserModulesResponse>(
    `/api/v1/permissions/users/${userId}/modules`
  );
}

/**
 * Set modules for a user
 */
export async function setUserModules(
  userId: number,
  modules: string[],
  inherit: boolean = false
): Promise<UserModulesResponse> {
  return apiClient.put<UserModulesResponse>(
    `/api/v1/permissions/users/${userId}/modules`,
    { modules, inherit }
  );
}

/**
 * Get plants assigned to a user
 */
export async function getUserPlants(
  userId: number
): Promise<UserPlantsResponse> {
  return apiClient.get<UserPlantsResponse>(
    `/api/v1/permissions/users/${userId}/plants`
  );
}

/**
 * Set plants for a user
 */
export async function setUserPlants(
  userId: number,
  plantIds: number[],
  inherit: boolean = false
): Promise<UserPlantsResponse> {
  return apiClient.put<UserPlantsResponse>(
    `/api/v1/permissions/users/${userId}/plants`,
    { plant_ids: plantIds, inherit }
  );
}

/**
 * Get charging stations assigned to a user
 */
export async function getUserChargingStations(
  userId: number
): Promise<UserChargingStationsResponse> {
  return apiClient.get<UserChargingStationsResponse>(
    `/api/v1/permissions/users/${userId}/charging-stations`
  );
}

/**
 * Set charging stations for a user
 */
export async function setUserChargingStations(
  userId: number,
  stationIds: number[],
  inherit: boolean = false
): Promise<UserChargingStationsResponse> {
  return apiClient.put<UserChargingStationsResponse>(
    `/api/v1/permissions/users/${userId}/charging-stations`,
    { charging_station_ids: stationIds, inherit }
  );
}

/**
 * Get EVI producers assigned to a user
 */
export async function getUserEVIProducers(
  userId: number
): Promise<UserEVIProducersResponse> {
  return apiClient.get<UserEVIProducersResponse>(
    `/api/v1/permissions/users/${userId}/evi-producers`
  );
}

/**
 * Set EVI producers for a user
 */
export async function setUserEVIProducers(
  userId: number,
  ids: number[],
  inherit: boolean = false
): Promise<UserEVIProducersResponse> {
  return apiClient.put<UserEVIProducersResponse>(
    `/api/v1/permissions/users/${userId}/evi-producers`,
    { evi_producer_ids: ids, inherit }
  );
}

/**
 * Get EVI consumers assigned to a user
 */
export async function getUserEVIConsumers(
  userId: number
): Promise<UserEVIConsumersResponse> {
  return apiClient.get<UserEVIConsumersResponse>(
    `/api/v1/permissions/users/${userId}/evi-consumers`
  );
}

/**
 * Set EVI consumers for a user
 */
export async function setUserEVIConsumers(
  userId: number,
  ids: number[],
  inherit: boolean = false
): Promise<UserEVIConsumersResponse> {
  return apiClient.put<UserEVIConsumersResponse>(
    `/api/v1/permissions/users/${userId}/evi-consumers`,
    { evi_consumer_ids: ids, inherit }
  );
}
